
import React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import ImageModal from "./ImageModal";
import TextModal from "./TextModal";
import { API_URL } from "../config";

// Reusable Table Component
const ServiceTable = ({ title, icon, gradient, data, searchTerm, onDelete, onImageClick, onLetterClick }) => {
  const filtered = data.filter(item => item.companyName?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 mb-8">
      <div className={`bg-gradient-to-r ${gradient} px-6 py-4`}>
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <span>{icon}</span> {title}
          <span className="ml-auto bg-white/20 px-3 py-1 rounded-full text-sm">{filtered.length} records</span>
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-white/5">
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Name</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Company</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">IMO</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Phone</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Email</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Location</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Passport</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Date</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Services</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Letter</th>
              <th className="px-3 py-3 text-left text-white/80 font-semibold text-sm">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-8 text-white/50">
                  <p>No records found</p>
                </td>
              </tr>
            ) : (
              filtered.map((item, index) => (
                <tr key={item.id} className={`border-b border-white/10 hover:bg-white/5 transition-colors
                  ${index % 2 === 0 ? 'bg-white/5' : ''}`}>
                  <td className="px-3 py-3 text-white font-medium text-sm">{item.name}</td>
                  <td className="px-3 py-3 text-cyan-300 text-sm">{item.companyName}</td>
                  <td className="px-3 py-3 text-white/80 text-sm">{item.IMO}</td>
                  <td className="px-3 py-3 text-white/80 text-sm">{item.phone}</td>
                  <td className="px-3 py-3 text-blue-300 text-sm">{item.email}</td>
                  <td className="px-3 py-3 text-white/80 text-sm">{item.location}</td>
                  <td className="px-3 py-3">
                    {item.passport ? (
                      <img
                        src={item.passport}
                        alt="passport"
                        onClick={() => onImageClick(item.passport)}
                        className="w-10 h-10 object-cover rounded-lg cursor-pointer hover:scale-110 
                          transition-transform border-2 border-white/20"
                      />
                    ) : (
                      <span className="text-white/40 text-xs">No image</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-white/80 text-sm">{item.date}</td>
                  <td className="px-3 py-3">
                    <span className="bg-blue-500/30 text-blue-200 px-2 py-1 rounded-full text-xs">
                      {item.services}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {item.supplyLitter && (
                      <button
                        onClick={() => onLetterClick(item.supplyLitter)}
                        className="bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded-lg 
                          transition-all text-xs flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onDelete(item.id)}
                      className="bg-red-500/20 hover:bg-red-500 text-red-300 hover:text-white 
                        p-2 rounded-lg transition-all duration-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function MarineServer() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);

  // Ship Supply
  const [booking, setBooking] = useState([]);
  useEffect(() => { fetchBooking(); }, []);
  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API_URL}/shipSupply`);
      setBooking(res.data);
    } catch (err) {
      console.error("error", err);
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_URL}/shipSupply/${id}`);
      fetchBooking();
    } catch (err) {
      console.error("error 2 ", err);
    }
  };

  // Ship Husbandry
  const [booking2, setBooking2] = useState([]);
  useEffect(() => { fetchBooking2(); }, []);
  const fetchBooking2 = async () => {
    try {
      const res = await axios.get(`${API_URL}/ShipHusbandry`);
      setBooking2(res.data);
    } catch (err) {
      console.error("error", err);
    }
  };
  const handleDelete2 = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_URL}/ShipHusbandry/${id}`);
      fetchBooking2();
    } catch (err) {
      console.error("error 2 ", err);
    }
  };

  // Civil Work
  const [booking3, setBooking3] = useState([]);
  useEffect(() => { fetchBooking3(); }, []);
  const fetchBooking3 = async () => {
    try {
      const res = await axios.get(`${API_URL}/CivilWork`);
      setBooking3(res.data);
    } catch (err) {
      console.error("error", err);
    }
  };
  const handleDelete3 = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_URL}/CivilWork/${id}`);
      fetchBooking3();
    } catch (err) {
      console.error("error 2 ", err);
    }
  };

  // Offshore Work
  const [booking4, setBooking4] = useState([]);
  useEffect(() => { fetchBooking4(); }, []);
  const fetchBooking4 = async () => {
    try {
      const res = await axios.get(`${API_URL}/OffshoreWork`);
      setBooking4(res.data);
    } catch (err) {
      console.error("error", err);
    }
  };
  const handleDelete4 = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_URL}/OffshoreWork/${id}`);
      fetchBooking4();
    } catch (err) {
      console.error("error 2 ", err);
    }
  };

  const totalRecords = booking.length + booking2.length + booking3.length + booking4.length;

  return (
    <div className="p-6">
      {/* Header Card */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white flex items-center gap-3">
              <span className="text-4xl">⚓</span> Marine Services
            </h2>
            <p className="text-cyan-200 mt-1">Manage all commercial marine service requests</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl px-4 py-2 text-white">
              <p className="text-xs opacity-80">Total</p>
              <p className="text-xl font-bold">{totalRecords}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl px-4 py-2 text-white">
              <p className="text-xs opacity-80">Supply</p>
              <p className="text-xl font-bold">{booking.length}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl px-4 py-2 text-white">
              <p className="text-xs opacity-80">Husbandry</p>
              <p className="text-xl font-bold">{booking2.length}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl px-4 py-2 text-white">
              <p className="text-xs opacity-80">Civil</p>
              <p className="text-xl font-bold">{booking3.length}</p>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-rose-500 rounded-xl px-4 py-2 text-white">
              <p className="text-xs opacity-80">Offshore</p>
              <p className="text-xl font-bold">{booking4.length}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl 
              text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
        </div>
      </div>

      {/* Tables */}
      <ServiceTable
        title="Ship Supply Services"
        icon="🚢"
        gradient="from-emerald-600 to-teal-600"
        data={booking}
        searchTerm={searchTerm}
        onDelete={handleDelete}
        onImageClick={setSelectedImage}
        onLetterClick={setSelectedLetter}
      />

      <ServiceTable
        title="Ship Husbandry Services"
        icon="🔧"
        gradient="from-purple-600 to-pink-600"
        data={booking2}
        searchTerm={searchTerm}
        onDelete={handleDelete2}
        onImageClick={setSelectedImage}
        onLetterClick={setSelectedLetter}
      />

      <ServiceTable
        title="Civil Work Services"
        icon="🏗️"
        gradient="from-orange-600 to-amber-600"
        data={booking3}
        searchTerm={searchTerm}
        onDelete={handleDelete3}
        onImageClick={setSelectedImage}
        onLetterClick={setSelectedLetter}
      />

      <ServiceTable
        title="Offshore Work Services"
        icon="🌊"
        gradient="from-red-600 to-rose-600"
        data={booking4}
        searchTerm={searchTerm}
        onDelete={handleDelete4}
        onImageClick={setSelectedImage}
        onLetterClick={setSelectedLetter}
      />

      {/* Modals */}
      {selectedImage && (
        <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
      )}

      {selectedLetter && (
        <TextModal text={selectedLetter} onClose={() => setSelectedLetter(null)} />
      )}
    </div>
  );
}

export default MarineServer;
